/* eslint-disable curly */
import mixpanel from 'mixpanel-browser';
import { analytics } from '../util/firebaseConfig';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { EVENT_MAPPING, Events } from './constants';

export const initialiseCurrentUserProperties = data => {
  const {
    id: { uuid: userId },
    attributes: {
      email,
      createdAt,
      profile: { firstName, lastName, displayName, publicData },
    },
  } = data;

  const userType = publicData?.userType || 'user';

  mixpanel.identify(userId);

  mixpanel.people.set({
    $email: email,
    $name: displayName,
    $first_name: firstName,
    $last_name: lastName,
    userType,
    createdAt,
  });

  // Set super properties
  mixpanel.register({
    userType,
    userID: userId,
  });
};

// Helper to safely set Meta Pixel external_id once Pixel is loaded
const setMetaUser = (userId, signupFrom) => {
  const trySet = () => {
    if (window.fbq && window.fbq.loaded) {
      try {
        window.fbq('set', 'external_id', userId);
        window.fbq('track', 'PageView', { signup_from: signupFrom });
        if (process.env.REACT_APP_MARKETPLACE_ROOT_URL !== 'https://nowoffers.app') {
          console.log('Meta Pixel set external_id:', userId);
        }
      } catch (e) {
        console.error('Meta Pixel set external_id error:', e);
      }
    } else {
      setTimeout(trySet, 100); // retry in 100ms
    }
  };
  trySet();
};

/**
 * Initialize or reinitialize current user across Mixpanel, Firebase, Meta Pixel, GTM.
 */
export const initialiseOrReinitUser = async ({
  firstName,
  lastName,
  email,
  userId,
  userType,
  signupFrom = "web",
  isSignup = false,
}) => {
  if (!userId) return;

  const displayName = `${firstName} ${lastName}`;
  const normalizedUserType = userType;

  // --- Mixpanel ---
  // Simplified ID Merge: identify() alone links this browser's anonymous
  // activity (pre-signup browsing included) to the user. The old reset()
  // before signup discarded that history; logout resets instead.
  mixpanel.identify(userId);

  // Base profile for Mixpanel People
  const baseProfile = {
    $name: displayName,
    $first_name: firstName,
    $last_name: lastName,
    user_type: normalizedUserType,
    user_id: userId,
    signup_from: signupFrom,
    ...(email && { $email: email }),
  };

  if (isSignup) {
    const now = new Date().toISOString();
    // Persist signup-related fields only once
    mixpanel.people.set_once({
      ...baseProfile,
      signup_time: now,
    });

    // Firebase Analytics user properties
    setUserProperties(analytics, {
      signup_time: now,
      signup_from: signupFrom,
    });
  } else {
    // Update existing Mixpanel profile with new data
    mixpanel.people.set({
      ...baseProfile,
      updated_at: new Date().toISOString(),
    });
  }

  // Super properties for Mixpanel (persist across events)
  mixpanel.register({
    user_type: normalizedUserType,
    user_id: userId,
    signup_from: signupFrom,
  });

  // --- Firebase Analytics ---
  setUserId(analytics, userId);
  const firebaseUserProps = {
    name: displayName,
    first_name: firstName,
    last_name: lastName,
    user_type: normalizedUserType,
    user_id: userId,
    signup_from: signupFrom,
    ...(email && { email }),
  };
  setUserProperties(analytics, firebaseUserProps);

  // --- Meta Pixel ---
  if (typeof window !== "undefined") {
    setMetaUser(userId, signupFrom);
  }

  // --- GTM ---
  if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
    try {
      window.dataLayer.push({
        event: "set_user",
        user_id: userId,
        user_properties: {
          name: displayName,
          email,
          user_type: normalizedUserType,
          signup_from: signupFrom,
        },
      });
    } catch (error) {
      console.error("GTM user setup error:", error);
    }
  }

  return Promise.resolve();
};



// Standard Meta Pixel events
const META_STANDARD_EVENTS = [
  'PageView',
  'Purchase',
  'AddToCart',
  'Lead',
  'CompleteRegistration',
  'ViewContent',
  'AddToWishlist',
  'InitiateCheckout',
  'AddPaymentInfo',
];

/**
 * Tracks an event across Mixpanel, Firebase, Meta Pixel, GTM.
 */
export const trackEventForUser = (eventName, properties = {}) => {
  const mapping = EVENT_MAPPING[eventName];
  if (!mapping) {
    console.warn(`No event mapping found for: ${eventName}`);
    return;
  }

  const timestamp = new Date().toISOString();
  const eventProps = {
    ...properties,
    device_type: 'web',
    platform: 'web',
    time_stamp: timestamp,
    ...(process.env.REACT_APP_MARKETPLACE_ROOT_URL !== 'https://nowoffers.app' && { debug_mode: 1 }),
  };

  if (process.env.REACT_APP_MARKETPLACE_ROOT_URL !== 'https://nowoffers.app') {
    console.log("eventProps", eventProps);
  }

  const userId = properties.user_id;

  // --- GA4 / Firebase ---
  if (userId) setUserId(analytics, userId);
  try {
    logEvent(analytics, mapping.firebase, eventProps);
  } catch (err) {
    console.error('Firebase tracking error:', err);
  }

  // --- Mixpanel ---
  try {
    mixpanel.track(mapping.mixpanel, eventProps);

    // Special handling: onboarding complete
    if (eventName === Events.SETUP_BUSINESS_PROFILE) {
      const now = new Date().toISOString();
      mixpanel.people.set({ onboard_time: now });
      setUserProperties(analytics, { onboard_time: now });
    }
  } catch (err) {
    console.error('Mixpanel tracking error:', err);
  }

  // --- Meta Pixel ---
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      if (META_STANDARD_EVENTS.includes(mapping.meta)) {
        window.fbq('track', mapping.meta, eventProps);
      } else {
        window.fbq('trackCustom', mapping.meta, eventProps);
      }
    } catch (err) {
      console.error('Meta Pixel tracking error:', err);
    }
  }

  // --- GTM ---
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    try {
      window.dataLayer.push({
        event: 'custom_event',
        eventName: mapping.firebase,
        ...eventProps,
      });
    } catch (err) {
      console.error('GTM tracking error:', err);
    }
  }
};

/**
 * Wrapper to call unified tracking.
 */
export const trackEventsMixpanel = (eventName, properties = {}) => {
  const mapping = EVENT_MAPPING[eventName];
  if (!mapping) {
    console.warn(`No event mapping found for: ${eventName}`);
    return;
  }

  // ✅ Just forward to unified tracker (no duplicate Mixpanel track)
  trackEventForUser(eventName, properties);
};

export const trackEventsTimeMixpanel = eventName => {
  const eventNameWithSuffix = `${eventName}_web`;
  mixpanel.time_event(eventNameWithSuffix);
};

export const resetMixpanelId = () => mixpanel.reset();

export const getMixpanelDistinctId = async () => {
  return mixpanel.get_distinct_id();
};